import React, { FC, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import { Box } from '@ch/common/components/box';
import { Text } from '@ch/common/components/text';
import { FormFile } from '@ch/common/components/form/file';
import { FormLabel } from '@ch/common/components/form/label';
import { FormSelect } from '@ch/common/components/form/select';
import { IconDelete, IconDocument } from '@ch/common/components/icon';
import { FormError } from '@ch/common/components/form/error';
import { Question, QuestionRequired } from '@ch/common/types/data';
import { useData } from '../use-data';
import { boxProps } from './const';
import { useQuizDocument } from './hooks/use-quiz-document';
import { QuizComplexFieldBlockStyled, QuizComplexFieldText, QuizComplexFieldWrapper } from './quiz.styled';
import QuizFieldHints from './quiz-field-hints';
import { FormInputField } from '@ch/common/components/form/input';
import { Button } from '@ch/common/components/button';

export const icon = <IconDocument $size={24} $color="primary" $m={[0, 10, 0, 0]} />;

const QuizFieldDocument: FC<Question> = ({
  identifier: id,
  required,
  placeholder,
  field_hints: hints,
  documentsMeta: meta,
}) => {
  const api = useContext(ContextApi);
  const { handleChange, handleDelete, handleUpload, documents, fieldMapping } = useQuizDocument(id, meta, api);
  const {
    data: {
      keywords: { fileChoose, fileRemove, uploadKey, nofileChosenText },
    },
  } = useData();

  const onUpload = () => {
    return handleUpload(api);
  };

  const requiredIdentifier = `${id}_required`;
  return (
    <>
      <QuizFieldHints hints={hints} />
      <QuizComplexFieldBlockStyled key={documents.length} {...{ ...boxProps, $m: [0, 0, 25] }}>
        {Object.entries(fieldMapping).map(([key, { title, options, descriptions }]) => (
          <Box $m={[0, 0, 20]} key={key}>
            <FormLabel>
              {title}{' '}
              {required === QuestionRequired.YES && (
                <Text as="span" $m={[0, 0, 0, 3]} $color="error" $size="lg">
                  *
                </Text>
              )}
            </FormLabel>
            <FormSelect
              name={key}
              hideError
              options={options}
              placeholder={placeholder}
              required={required === QuestionRequired.YES}
            />
            <Text as="span" $color="primary" $size="sm" $m={[10, 5]}>
              {descriptions?.find(({ value }) => value === api.getValue(key))?.description}
            </Text>
            <FormError name={key} />
          </Box>
        ))}
        <Box $m={[0, 0, 15]}>
          <FormFile
            {...{
              name: id,
              selectButtonTitle: fileChoose,
              uploadButtonTitle: uploadKey,
              removeButtonTitle: fileRemove,
              icon,
              onChange: handleChange,
              onUpload,
              multiple: true,
              nofileChosenText,
            }}
          />
          <FormInputField name={requiredIdentifier} required type="hidden" />
        </Box>
      </QuizComplexFieldBlockStyled>
      {documents.map(({ url, file: { name }, values }, index) => (
        <QuizComplexFieldBlockStyled key={index} {...{ ...boxProps, $m: [0, 0, 25] }}>
          <Box $display="flex" $alignItems="center" $justifyContent="space-between" $m={[0, 0, 15]}>
            <QuizComplexFieldWrapper>
              {icon}
              <QuizComplexFieldText $size="sm" $m={[0]} title={name}>
                {name}
              </QuizComplexFieldText>
            </QuizComplexFieldWrapper>
            <Button
              $variant="icon"
              $size="ico-xs"
              $color="primary"
              type="button"
              onClick={() => {
                handleDelete(url, api);
              }}
              title={fileRemove}
              $m={[0, 0, 0, 30]}
            >
              <IconDelete $size={14} $color="primary" />
            </Button>
          </Box>
          {Object.entries(fieldMapping).map(
            ([key, { title }]) =>
              values[key] && (
                <Box $m={[0, 0, 15]} key={key}>
                  <FormLabel>
                    <Text as="span" $color="primary" $fontFamily="aeonikBold" $size="sm" $m={[0]}>
                      {title}:
                    </Text>{' '}
                    {values[key]}
                  </FormLabel>
                </Box>
              ),
          )}
        </QuizComplexFieldBlockStyled>
      ))}
    </>
  );
};

export default QuizFieldDocument;
